<template>
    <div class="h-full">
        <ai-grid-edit-wrapper
            ref="gridRef"
            :meta="meta"
            :to-remove-toolbar-items-names="toRemoveToolbarItemsNames"
            :hasnt-route="true"
            :toolbar-user-items="toolbarUserItems"
            @grid-edit-wrapper-mounted="onGridEditWrapperMounted"
            @refresh="onRefresh"
            @delete-click="onDeleteClick"
            @editor-preparing="onEditorPreparing"
        />
    </div>
</template>

<script>
export default {
    name: 'users-blockages',
};
</script>

<script setup>
import { ref, computed } from 'vue';
import AiGridEditWrapper from '@/components/wrappers/ai-grid-edit-wrapper.vue';
import usersLocalStore from '@/views/administration/users/store.js';
import environmentStore from '@/store/enviroment-data.js';
import { getLockReasons, getLocks } from '@/service/admin-service.js';

const { state, UI_ELEMENTS_IDS } = usersLocalStore;
const { disableUiElement } = environmentStore;

const gridRef = ref();
const gridInstance = ref();
const isEditable = !disableUiElement(UI_ELEMENTS_IDS.BLOCKAGES);

const useValidation = ref(false);

const toRemoveToolbarItemsNames = state.userCardPopupVisible ?
    ['save', 'choice', 'edit', 'copy', 'delete', 'refresh', ] :
    ['save', 'choice', 'edit', 'copy', 'delete'];

const onGridEditWrapperMounted = async () => {
    gridInstance.value = gridRef.value.ref.gridInstance;
    state.lockTypes = await getLockReasons();
    disableAddButton();
    checkDisableCopyAndDeleteButtons({ selectedRowKeys: [] });
    gridRef.value.ref.gridInstance.on('selectionChanged', checkDisableCopyAndDeleteButtons);
    if (!state.userCardPopupVisible) await getGridDataSource();
    else toRemoveToolbarItemsNames.push('refresh');
    await setGridDataSource();
    if (state.editingChanges.length > 0) {
        gridRef.value.ref.gridInstance.option('editing.changes', state.editingChanges);
    }
};

const disableAddButton = () => {
    if (!isEditable) {
        gridRef.value.ref.gridRef.setRefValue('isAddButtonDisabled', true);
    }
}

const disableCopyAndDeleteButtons = () => {
    if (!isEditable) {
        gridRef.value.ref.gridRef.setRefValue('isCustomCopyButtonDisabled', true);
        gridRef.value.ref.gridRef.setRefValue('isCustomDeleteButtonDisabled', true);
        return true;
    }
    return false;
}

const checkDisableCopyAndDeleteButtons = (e) => {
    const isButtonDisabled = disableCopyAndDeleteButtons();
    if (isButtonDisabled) return;

    if (e.selectedRowKeys.length) {
        gridRef.value.ref.gridRef.setRefValue('isCustomCopyButtonDisabled', false);
        gridRef.value.ref.gridRef.setRefValue('isCustomDeleteButtonDisabled', false);
    } else {
        gridRef.value.ref.gridRef.setRefValue('isCustomCopyButtonDisabled', true);
        gridRef.value.ref.gridRef.setRefValue('isCustomDeleteButtonDisabled', true);
    }
}

const onRefresh = async () => {
    await getGridDataSource();
    await setGridDataSource();
};

const getGridDataSource = async () => {
    const response = await getLocks(state.userRequestBody.id, true);
    state.userRequestBody.locks = response.data;
    state.userRequestBody.locks = state.userRequestBody.locks?.map(lock => {
        return { ...lock, user_id: state.userRequestBody.id };
    });
};

const afterSaveInUsersPopup = () => {
    gridRef.value.ref.gridRef.refreshClick();
};

const setGridDataSource = async () => {
    await gridRef.value.ref.setDataSource(state.userRequestBody.locks);
};

const prepareLocksForSave = () => {
    const changes = gridRef.value.ref.gridInstance.option('editing.changes');
    const updatedLocks = changes.filter(lock => lock.type === 'update');
    const insertLocks = changes.filter(lock => lock.type === 'insert');
    let locks = Array.from(state.userRequestBody.locks);

    updatedLocks.forEach(updatedLock => {
        const index = locks.findIndex(lock => lock.id === updatedLock.key);
        if (index !== -1) {
            locks[index] = {
                ...locks[index],
                ...updatedLock.data,
            };
        }
    });

    insertLocks.forEach(insertLock => {
        const newLock = {
            ...insertLock.data,
            user_id: state.userRequestBody.id,
        };
        locks.push(newLock);
    });

    return locks;
};

const onDeleteClick = async (rowsToDelete) => {
    let changes = gridRef.value.ref.gridInstance.option('editing.changes');
    changes = changes.filter(change => !rowsToDelete.deleteDataArgs.some(arg => arg.greyEminence === change.key));
    gridRef.value.ref.gridInstance.option('editing.changes', changes);
    const selected = rowsToDelete.deleteDataArgs.filter(item => item.id).map(item => item.id);
    if (selected.length > 0) {
        state.userRequestBody.deleted_locks.push(...selected);
        state.userRequestBody.locks = state.userRequestBody.locks.filter(lock => !selected.includes(lock.id));
        await setGridDataSource();
    }
    removeFocus();
};

const removeFocus = () => {
    gridInstance.value.option('focusedColumnIndex', -1);
    gridInstance.value.option('focusedRowIndex', -1);
    gridInstance.value.option('focusedRowKey', null);
};

const checkDateBegLessDateEnd = () => {
    let isDateBegLessDateEnd = true;
    gridInstance.value.option('onCellPrepared', null);

    gridInstance.value.option('onCellPrepared', function(e) {
        if (e.rowType === 'data' &&
            e.column.dataField === 'dt_end' &&
            e.data.dt_beg &&
            e.data.dt_end &&
            new Date(e.data.dt_beg) > new Date(e.data.dt_end)) {
            isDateBegLessDateEnd = false;
        }
    });
    return isDateBegLessDateEnd;
};

const checkRequiredFieldsValidation = () => {
    useValidation.value = true;
};

const onEditorPreparing = (event) => {
    if (event.parentType === 'dataRow') {
        event.editorOptions.onFocusOut = null;
    }
};

const toolbarUserItems = [
    {
        widget: 'dx-button',
        index: 0,
        component: {
            name: 'copy',
            disabledRefName: 'isCustomCopyButtonDisabled',
            hint: 'Создать копию сфокусированной строки',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-copy-base',
        },
    },
    {
        widget: 'dx-button',
        index: 2,
        component: {
            name: 'delete',
            disabledRefName: 'isCustomDeleteButtonDisabled',
            hint: 'Удалить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-delete-base',
        },
    },
];

const meta = computed(() => ({
    keyExpr: 'id',
    focusedRowEnabled: true,
    searchPanelEnabled: false,
    exportEnabled: false,
    settingsEnabled: false,
    columnChooserEnabled: false,
    dontShowDeleteConfirmDialog: true,
    cancelRefreshConfirmDialog: true,
    cancelDeleteOneRow: true,
    api: {
        select: 'admin./user-locks/get',
    },
    rights: {
        INS: isEditable,
        UPD: isEditable,
        DEL: isEditable,
    },
    columns: [
        {
            name: 'dt_beg',
            caption: 'Дата начала',
            dataField: 'dt_beg',
            dataType: 'datetime',
            allowEditing: isEditable,
            visible: true,
            width: '25%',
            validationRules: useValidation.value ?
                [
                    {
                        type: 'required',
                        message: 'Значение поля не должно быть пустым',
                    },
                ] : [],
            calculateDisplayValue: (rowData) => {
                if (!rowData.dt_beg) return null;
                const date = new Date(rowData.dt_beg);
                return date.toLocaleString('ru-RU', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            },
        },
        {
            name: 'dt_end',
            caption: 'Дата окончания',
            dataField: 'dt_end',
            dataType: 'datetime',
            allowEditing: isEditable,
            visible: true,
            width: '25%',
            validationRules: [{
                type: 'custom',
                validationCallback: (rowData) => {
                    if (!rowData.data.dt_beg || !rowData.data.dt_end) return true;
                    return new Date(rowData.data.dt_beg) < new Date(rowData.data.dt_end);
                },
                message: 'Дата окончания не может быть раньше даты начала',
            }],
            calculateDisplayValue: (rowData) => {
                if (!rowData.dt_end) return null;
                const date = new Date(rowData.dt_end);
                return date.toLocaleString('ru-RU', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            },
        },
        {
            name: 'lock_type_id',
            caption: 'Тип блокировки',
            dataField: 'lock_type_id',
            allowEditing: isEditable,
            visible: true,
            width: '25%',
            validationRules: useValidation.value ? [{
                type: 'required',
                message: 'Значение поля не должно быть пустым',
            }] : [],
            lookup: {
                dataSource: state.lockTypes,
                valueExpr: 'id',
                displayExpr: 'name',
            },
        },
        {
            name: 'reason',
            caption: 'Описание',
            dataField: 'reason',
            allowEditing: isEditable,
            visible: true,
            width: '25%',
        },
    ],
}));

defineExpose({
    gridInstance,
    setGridDataSource,
    prepareLocksForSave,
    afterSaveInUsersPopup,
    checkRequiredFieldsValidation,
    checkDateBegLessDateEnd,
});
</script>
